import { GetString } from 'fluent-react';
import React, {RefObject, useState} from 'react';
import { Transition } from 'react-transition-group';
import styled, { keyframes } from 'styled-components';
import { Location } from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { SlideElementProps } from '../../presentationManager/PresentationManager';
import { getShareFunctions } from '../../sharedComponents/newExportsPanel/Share';
import { googleAnalyticsEvent } from '../../useGoogleAnalytics';
import { HelpTipsTriggerContainer } from '../Grid';
import { smallMediaQuery } from '../Grid';
import { primaryColor1 } from '../Utils';
import VideoTutorial, {
  Chapter,
  timeToTimeStamp,
} from './VideoTutorial';

const query = gql`
  query countryName($countryId: ID!) {
    location(id: $countryId) {
      id
      shortName
    }
  }
`;

interface SuccessResponse {
  location: {
    id: Location['id'];
    shortName: Location['shortName'];
  };
}

interface Variables {
  countryId: string;
}

const Container = styled(HelpTipsTriggerContainer)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 0.5rem;
`;

const iconSize = 24; // in px
const iconSizeSmall = 18; // in px

export const Icon = styled.div`
  border-radius: 400rem;
  color: white;
  background-color: ${primaryColor1};
  height: ${iconSize}px;
  width: ${iconSize}px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  @media ${smallMediaQuery} {
    height: ${iconSizeSmall}px;
    width: ${iconSizeSmall}px;
  }
`;

const ToggleIcon = styled(Icon)`
  margin-bottom: 0.75rem;

  &:hover {
    cursor: pointer;
  }
`;

const ShareIcon = styled(ToggleIcon)`
  padding: 6px;

  svg {
    fill: #fff;
    width: 100%;
  }

  @media ${smallMediaQuery} {
    margin: 0.4rem 0;

    svg {
      width: 175%;
      position: relative;
      left: -3px;
    }
  }
`;

const IconContainer = styled.div`
  position: relative;
`;

const videoLabelWidth = 240; // in px
const socialButtonWidth = 18; // in px
const socialButtonMargin = 5; // in px
const numSocialIcons = 5; // Update this number if more social icons are addded
const socialMenuWidth = (socialButtonMargin * 2) + (socialButtonWidth + (socialButtonMargin * 2)) * numSocialIcons;
const socialSlideDuration = 300;
const videoSlideDuration = 400;

enum SlideTransitionDirection {
  entering = 'entering',
  entered = 'entered',
  exiting = 'exiting',
  exited = 'exited',
}

interface SlideTransitionStyles {
  entering: React.CSSProperties;
  entered: React.CSSProperties;
  exiting: React.CSSProperties;
  exited: React.CSSProperties;
}

const videoLabelTransitionStyles: SlideTransitionStyles = {
  entering: { width: videoLabelWidth },
  entered:  { width: videoLabelWidth },
  exiting:  { width: 0 },
  exited:  { width: 0 },
};

const socialLabelTransitionStyles: SlideTransitionStyles = {
  entering: { width: socialMenuWidth },
  entered:  { width: socialMenuWidth },
  exiting:  { width: 0 },
  exited:  { width: 0 },
};

const Label = styled.div`
  background-color: ${primaryColor1};
  color: #fff;
  font-size: 0.85rem;
  position: absolute;
  top: 0;
  left: 10px;
  transform: translateX(-100%);
  height: ${iconSize}px;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;

  @media ${smallMediaQuery} {
    height: ${iconSizeSmall}px;
  }
`;

const VideoLink = styled(Label)`
  width: ${videoLabelWidth}px;
  transition: all ${videoSlideDuration}ms ease-in-out;
  cursor: pointer;

  @media ${smallMediaQuery} {
    width: 200px;
  }
`;

const SocialMenu = styled(Label)`
  width: ${socialMenuWidth}px;
  transition: all ${socialSlideDuration}ms ease-in-out;
`;

const SvgContainer = styled.div`
  display: contents;

  svg {
    fill: #fff;
    width: ${socialButtonWidth}px;
    margin: 0 ${socialButtonMargin}px;
    cursor: pointer;
  }
`;

const copyAnimation = keyframes`
  from {
    transform: translate(-70%, 0%) scale(0.3);
  }

  to {
    transform: translate(-70%, -50%) scale(1);
  }
`;

const CopySuccess = styled.div`
  position: absolute;
  white-space: nowrap;
  left: 0;
  transform: translate(-70%, -50%);
  background-color: #fff;
  box-shadow: 1px 1px 5px 1px rgba(0, 0, 0, 0.15);
  font-size: 0.9rem;
  padding: 3px;
  animation: ${copyAnimation} 0.08s ease-out 1;
`;

interface Props extends SlideElementProps {
  getFluentString: GetString;
  zIndex: number;
  toggleHelpTooltips: () => void;
  helpTooltipsActive: boolean;
  countryId: string;
  videoChapter: Chapter;
}

const Trigger = React.forwardRef((props: Props, rootElRef: RefObject<HTMLDivElement>) => {
  const {
    zIndex, helpTooltipsActive, toggleHelpTooltips, getFluentString, countryId,
    videoChapter,
  } = props;
  const variables: Variables = {
    countryId,
  };
  const [shareBtnActive, setShareBtnActive] = useState<boolean>(false);
  const [isVideoTutorialVisible, setVideoTutorialVisible] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<'copied' | null>(null);
  const toggleShareBtn = () => setShareBtnActive(!shareBtnActive);
  const style: React.CSSProperties = { zIndex };
  const helpIconStyle: React.CSSProperties = {
    fontSize: helpTooltipsActive === true ? '1.2rem' : '1rem',
  };
  const helpIcon = helpTooltipsActive === true ? '×' : '?';

  const url = window.location.href;
  const {shareFacebook, shareTwitter, shareLinkedIn, shareEmail} = getShareFunctions(url);

  const copyToClipboard = () => {
    const hiddenInputForUrl = document.createElement('input');
    document.body.appendChild(hiddenInputForUrl);
    hiddenInputForUrl.value = url;
    hiddenInputForUrl.select();
    document.execCommand('copy');
    document.body.removeChild(hiddenInputForUrl);
    setCopySuccess('copied');
    setTimeout(() => setCopySuccess(null), 4000);
  };

  const videoLink = (state: SlideTransitionDirection) => {
    const videoLinkOnClick = () => {
      setVideoTutorialVisible(true);
      googleAnalyticsEvent('Country Profiles Video', 'click -- video link');
    };
    return (
      <VideoLink
        style={videoLabelTransitionStyles[state]}
        onClick={videoLinkOnClick}
      >
        <SvgContainer dangerouslySetInnerHTML={{__html: require('./video-icon.svg')}} />
        {getFluentString('help-tooltips-tutorial-video-link')}
      </VideoLink>
    );
  };

  const videoTimeStamp = timeToTimeStamp(videoChapter.minute, videoChapter.second);
  const videoTutorial = isVideoTutorialVisible === true ? (
    <VideoTutorial closeModal={() => setVideoTutorialVisible(false)} initialTimeStamp={videoTimeStamp} />
  ) : null;

  const shareIcon = shareBtnActive === true ? '×' : (
    <div dangerouslySetInnerHTML={{__html: require('./share-icon.svg')}} />
  );

  const copySuccessMessage = copySuccess === 'copied' ? (
    <CopySuccess>{getFluentString('share-bar-copied-to-clipboard-success')}</CopySuccess> ) : null;

  let output: React.ReactElement<any> | null;
  const {loading, error, data} = useQuery<SuccessResponse, Variables>(query, {variables});
  if (loading === true) {
    output = null;
  } else if (error !== undefined) {
    console.error(error);
    output = null;
  } else if (data !== undefined) {
    const {location} = data;
    if (location === null) {
      output = null;
    } else {
      const {shortName} = location;
      const socialMenu = (state: SlideTransitionDirection) => {
        const onClickLinkedIn = () => {
          shareLinkedIn(
            getFluentString('share-social-default-title', {
              'country-name': shortName,
              'url': url,
            }), getFluentString('share-social-default-summary'),
          );
          googleAnalyticsEvent('Country Profiles Share', 'click -- linkedin');
        };
        const onClickEmail = () => {
          shareEmail(getFluentString('share-social-email-subject-line', {'country-name': shortName}),
            getFluentString('share-social-default-summary'),
            getFluentString('share-soical-default-line-2', { url }),
          );
          googleAnalyticsEvent('Country Profiles Share', 'click -- email');
        };
        const onClickCopy = () => {
          copyToClipboard();
          googleAnalyticsEvent('Country Profiles Share', 'click -- copy url');
        };
        const onClickFacebook = () => {
          shareFacebook();
          googleAnalyticsEvent('Country Profiles Share', 'click -- facebook');
        };
        const onClickTwitter = () => {
          shareTwitter(getFluentString('share-social-twitter-title', { 'country-name': shortName }) );
          googleAnalyticsEvent('Country Profiles Share', 'click -- twitter');
        };
        return (
          <SocialMenu style={socialLabelTransitionStyles[state]}>
            <SvgContainer
              dangerouslySetInnerHTML={{__html: require('../../img/icons/social/linkedin.svg')}}
              onClick={onClickLinkedIn}
            />
            <SvgContainer
              dangerouslySetInnerHTML={{__html: require('./mail-icon.svg')}}
              onClick={onClickEmail}
            />
            <SvgContainer
              dangerouslySetInnerHTML={{__html: require('./link-icon.svg')}}
              onClick={onClickCopy}
            />
            <SvgContainer
              dangerouslySetInnerHTML={{__html: require('../../img/icons/social/facebook.svg')}}
              onClick={onClickFacebook}
            />
            <SvgContainer
              dangerouslySetInnerHTML={{__html: require('../../img/icons/social/twitter.svg')}}
              onClick={onClickTwitter}
            />
          </SocialMenu>
        );
      };
      output = (
        <>
          <Transition
            in={shareBtnActive}
            timeout={socialSlideDuration}
            children={socialMenu}
          />
        </>
      );
    }
  } else {
    output = null;
  }

  return (
    <Container ref={rootElRef} style={style}>
      <IconContainer>
        <Transition
          in={helpTooltipsActive}
          timeout={videoSlideDuration}
          children={videoLink}
        />
        <ToggleIcon onClick={toggleHelpTooltips} style={helpIconStyle}>{helpIcon}</ToggleIcon>
      </IconContainer>
      <IconContainer>
        {output}
        <ShareIcon onClick={toggleShareBtn}>{shareIcon}</ShareIcon>
      </IconContainer>
      {copySuccessMessage}
      {videoTutorial}
    </Container>
  );
});

export default Trigger;
